import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { adminApi, Tenant, TenantUsage } from '../lib/api'
import {
  ArrowLeft,
  Building2,
  ExternalLink,
  Edit2,
  Trash2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Database,
  Server,
  HardDrive,
} from 'lucide-react'

export default function TenantDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [usage, setUsage] = useState<TenantUsage[]>([])
  const [loading, setLoading] = useState(true)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (id) {
      fetchTenant()
    }
  }, [id])

  async function fetchTenant() {
    setLoading(true)
    try {
      const res = await adminApi.getTenant(id!)
      if (res.success) {
        setTenant(res.tenant)
        setUsage(res.usage || [])
      }
    } catch (err) {
      console.error('Failed to load tenant:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleStatusChange = async (newStatus: 'active' | 'suspended') => {
    if (!tenant) return
    try {
      await adminApi.updateTenant(tenant.id, { status: newStatus })
      setTenant({ ...tenant, status: newStatus })
    } catch (err) {
      console.error('Failed to update status:', err)
    }
  }

  const handleDelete = async () => {
    if (!tenant) return
    setDeleting(true)
    try {
      await adminApi.deleteTenant(tenant.id)
      navigate('/tenants')
    } catch (err) {
      console.error('Failed to delete tenant:', err)
    } finally {
      setDeleting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    )
  }

  if (!tenant) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Tenant not found</p>
        <Link to="/tenants" className="btn-secondary mt-4">
          Back to Tenants
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/tenants"
            className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-gray-700 rounded-lg flex items-center justify-center">
              <Building2 className="h-6 w-6 text-gray-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{tenant.company_name}</h1>
              <p className="text-gray-400">{tenant.slug}</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary">
            <Edit2 className="h-4 w-4 mr-2" />
            Edit
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="btn-danger"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </button>
        </div>
      </div>

      {/* Status and Actions */}
      <div className="card p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span
            className={`badge-${
              tenant.status === 'active'
                ? 'green'
                : tenant.status === 'provisioning'
                ? 'yellow'
                : 'red'
            } px-3 py-1`}
          >
            {tenant.status === 'active' && <CheckCircle className="h-4 w-4 mr-1" />}
            {tenant.status === 'suspended' && <XCircle className="h-4 w-4 mr-1" />}
            {tenant.status === 'provisioning' && <AlertTriangle className="h-4 w-4 mr-1" />}
            {tenant.status}
          </span>
          <span
            className={`badge-${
              tenant.tier === 'enterprise'
                ? 'purple'
                : tenant.tier === 'pro'
                ? 'blue'
                : 'gray'
            }`}
          >
            {tenant.tier}
          </span>
        </div>
        <div className="flex gap-2">
          {tenant.status === 'active' && (
            <button
              onClick={() => handleStatusChange('suspended')}
              className="btn-secondary text-sm"
            >
              Suspend Tenant
            </button>
          )}
          {tenant.status === 'suspended' && (
            <button
              onClick={() => handleStatusChange('active')}
              className="btn-success text-sm"
            >
              Activate Tenant
            </button>
          )}
          <a
            href={`https://${tenant.subdomain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-sm"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Open Portal
          </a>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Info */}
        <div className="card">
          <div className="p-6 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white">General Information</h2>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="text-sm text-gray-400">Company Name</label>
              <p className="text-white">{tenant.company_name}</p>
            </div>
            <div>
              <label className="text-sm text-gray-400">Contact Email</label>
              <p className="text-white">{tenant.contact_email}</p>
            </div>
            <div>
              <label className="text-sm text-gray-400">Subdomain</label>
              <p className="text-white font-mono">{tenant.subdomain}</p>
            </div>
            <div>
              <label className="text-sm text-gray-400">Created</label>
              <p className="text-white">
                {new Date(tenant.created_at).toLocaleString()}
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-400">Version</label>
              <p className="text-white font-mono">{tenant.current_version || 'v1.0.0'}</p>
            </div>
          </div>
        </div>

        {/* Infrastructure */}
        <div className="card">
          <div className="p-6 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white">Infrastructure</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <Server className="h-5 w-5 text-gray-500" />
              <div>
                <label className="text-sm text-gray-400">Worker Name</label>
                <p className="text-white font-mono text-sm">{tenant.worker_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Database className="h-5 w-5 text-gray-500" />
              <div>
                <label className="text-sm text-gray-400">Database</label>
                <p className="text-white font-mono text-sm">{tenant.db_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <HardDrive className="h-5 w-5 text-gray-500" />
              <div>
                <label className="text-sm text-gray-400">R2 Bucket</label>
                <p className="text-white font-mono text-sm">{tenant.r2_bucket}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Usage History */}
      <div className="card">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">Usage History</h2>
        </div>
        {usage.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-800/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    API Requests
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    Storage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    Users
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    Controls
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {usage.map((u) => (
                  <tr key={u.id}>
                    <td className="px-6 py-4 text-sm text-gray-300">{u.date}</td>
                    <td className="px-6 py-4 text-sm text-gray-300">
                      {u.api_requests.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">
                      {(u.storage_bytes / 1024 / 1024).toFixed(2)} MB
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">{u.users_count}</td>
                    <td className="px-6 py-4 text-sm text-gray-300">{u.controls_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center text-gray-500">
            No usage data available yet
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/75"
            onClick={() => setShowDeleteModal(false)}
          />
          <div className="relative bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
            <div className="flex items-center gap-3 text-red-400 mb-4">
              <AlertTriangle className="h-6 w-6" />
              <h3 className="text-lg font-semibold">Delete Tenant</h3>
            </div>
            <p className="text-gray-300 mb-4">
              Are you sure you want to delete <strong>{tenant.company_name}</strong>?
              This action cannot be undone and will:
            </p>
            <ul className="text-sm text-gray-400 space-y-1 mb-6 ml-4 list-disc">
              <li>Remove all tenant data</li>
              <li>Delete the D1 database</li>
              <li>Remove the R2 bucket</li>
              <li>Deprovision the worker</li>
            </ul>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="btn-danger flex-1"
              >
                {deleting ? 'Deleting...' : 'Delete Tenant'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
